import React, { useState } from "react";
import { useI18n } from "../../i18n/i18nService";
import { useAuth } from "../../contexts/AuthContext";
import { getHashedDeviceId } from "../../utils/hash";
import { usePhoneValidation } from "../../utils/phoneNumber"
import { useNavigate } from "react-router-dom";
import { FlagLanguageSelector } from "../../components/LanguageSelector";
import ThemeToggle from "../../components/ThemeToggle";
import { ButtonCustom, FormError, InputField, ThemedCheckbox } from "../../components/common/Form";


const RegisterPage = () => {
    const { t, i18n } = useI18n(['auth']);
    const { register } = useAuth(); // Assure-toi que useAuth contient register
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        fullname: "",
        phone: "",
        acceptTerms: false,
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { validate: isValidPhone, format: formatPhone, findDetail } = usePhoneValidation();

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "phone" && type === "tel") {
            // Formater le numéro de téléphone
            const raw = value.replace(/[^\d+]/g, '');
            setFormData((prev) => ({ ...prev, phone: formatPhone(raw) }));
            console.log(findDetail(raw));
        } else {

            setFormData((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccess(false);

        if (!isValidPhone(formData.phone)) {
            setError(t("auth:errors.register.invalid_phone"));
            return;
        }
        if (!formData.acceptTerms) {
            setError(t("auth:errors.register.accept_terms"));
            return;
        };

        setLoading(true);
        const deviceId = await getHashedDeviceId();
        try {
            const cleanedPhone = formData.phone.replace(/\s+/g, '');
            formData.phone = cleanedPhone;
            await register({
                ...formData,
                deviceId,
            });
            navigate("/otp", {  state: { phone: cleanedPhone }, replace: true });
        } catch (error) {
            console.log(error);
            setError(t(`auth:errors.register.${error.status || 500}`));
            return;
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-light-background dark:bg-dark-background px-4">
            <div className="max-w-md w-full p-6 relative rounded-lg shadow-lg bg-white dark:bg-gray-800">
                {/* Logo */}
                <div className="flex justify-center items-center mb-4">
                    <img src="/images/logo.png" alt="IDARAF" className="h-28 w-28 object-contain" />
                </div>

                {/* Formulaire */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-2xl font-bold mb-6 text-center text-light-text/80 dark:text-dark-text">{t("auth:sign_up")}</h2>
                    {error && <FormError message={error} />}
                    {success && <div className="text-green-500">{t("register_success")}</div>}
                    <InputField
                        name="fullname"
                        label={t("auth:fullname")}
                        value={formData.fullname}
                        onChange={handleChange}
                        autoComplete="fullname"
                        required
                    />

                    <InputField
                        type="tel"
                        name="phone"
                        label={t("auth:phone_number")}
                        placeholder={t("auth:phone_number_placeholder")}
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="username"
                        required
                    />
                    {formData.phone && (
                        <span className={isValidPhone(formData.phone) ? 'text-xs text-light-primary dark:text-dark-primary' : 'text-xs text-light-error dark:text-dark-error'}>
                            {isValidPhone(formData.phone) ? '✓ ' + t("auth:phone_number_valid") : '✗ ' + t("auth:phone_number_invalid")}
                        </span>
                    )}

                    {/* Checkbox Termes */}
                    <label className="flex items-center space-x-2 text-sm text-light-text dark:text-dark-text">
                        <ThemedCheckbox
                            name="acceptTerms"
                            id="acceptTerms"
                            checked={formData.acceptTerms}
                            onChange={handleChange}
                            required
                        />
                        <span>
                            {t("auth:accept_terms")}{" "}
                            <a href="/terms" className="text-light-primary dark:text-dark-primary  hover:underline">
                                {t("auth:terms_conditions")}
                            </a>
                        </span>
                    </label>

                    {/* Bouton */}
                    <ButtonCustom
                        type="submit"
                        variant="primary"
                        className="w-full py-2 px-4 rounded-md text-white font-semibold"
                        loading={loading}
                        disabled={loading || !formData.acceptTerms}
                    >
                        {t("auth:submit")}
                    </ButtonCustom>
                    <div className="flex justify-between items-center mt-4">
                        <div className="text-sm text-gray-600 dark:text-gray-400  ">
                            {t("auth:already_have_an_account")}{" "}
                            <a href="/login" className="text-light-primary dark:text-dark-primary hover:underline">
                                {t("auth:sign_in")}
                            </a>
                        </div>
                        <ThemeToggle />
                    </div>
                </form>

                <div className="absolute top-5 right-5 flex items-center justify-center">
                    <FlagLanguageSelector
                        currentLanguage={i18n.language}
                        onChange={changeLanguage}
                    />
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
